import { getPortalSdk } from 'portal-sdk';
import { useQuery } from 'react-query';
import { usePortalUser } from 'vet-bones/utils/portal';

// this type needs to match the type returned by useCloudStorageProjects but with the added isCloudStorageProject flag that is always true
export type CloudProject = NonNullable<
  ReturnType<typeof useCloudStorageProjects>['data']
>[number] & {
  isCloudStorageProject: true;
};

export const CLOUD_PROJECT_LIST_QUERY_KEY = 'useCloudStorageProjects';

export const getCloudProjectListQueryKey = (
  isProfileLoading = false,
  isCloudStorageEnabled = true
) => [CLOUD_PROJECT_LIST_QUERY_KEY, isProfileLoading, isCloudStorageEnabled];

export const useCloudStorageProjects = () => {
  const portalSdk = getPortalSdk();
  const { data: profileData, isLoading: isProfileLoading } = usePortalUser();
  const isCloudStorageEnabled = Boolean(
    profileData?.is_designer_storage_enabled
  );

  const { data, isLoading } = useQuery(
    getCloudProjectListQueryKey(isProfileLoading, isCloudStorageEnabled),
    async () => {
      if (isProfileLoading) {
        return null;
      }

      if (!isCloudStorageEnabled || !portalSdk) {
        return [];
      }

      const projects = await portalSdk.getDesignerProjects();
      // filter to only include this users projects
      const userProjects = projects
        .filter((project) => project.owner?.id === profileData?.id)
        .map((project) => ({
          ...project,
          isCloudStorageProject: true as const,
        }));
      return userProjects;
    },
    { initialData: null }
  );
  const formattedData = data?.map((project) => ({
    ...project,
    connectionId: project.connection_id || undefined,
    dateCreated: new Date(project.created_at).getTime(),
    dateLastUpdated: new Date(project.updated_at).getTime(),
  }));

  return {
    data: formattedData || null,
    isLoading: isProfileLoading || isLoading,
  };
};
